import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, CircularProgress, TextField, IconButton,
  ToggleButtonGroup, ToggleButton, Chip, Collapse, Grid
} from '@mui/material';
import {
  Home, Print, Search, Warehouse, Inventory2, KeyboardArrowDown, KeyboardArrowRight
} from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card elevation={2} sx={{ borderLeft: `4px solid ${color}`, height: '100%' }}>
    <CardContent sx={{ py: 1.5 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6" fontWeight={800} color={color} lineHeight={1}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
        <Icon sx={{ color, fontSize: 26 }} />
      </Box>
    </CardContent>
  </Card>
);

const InventoryValuation = () => {
  const navigate = useNavigate();
  const [stock,   setStock]   = useState({});
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [mode,    setMode]    = useState('valuation'); // valuation | availability
  const [q,       setQ]       = useState('');
  const [open,    setOpen]    = useState({}); // expanded rows in availability mode

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/payables/item-stock`, { headers: hdrs() }),
      axios.get(`${API}/api/payables/items`,      { headers: hdrs() }),
    ]).then(([s, i]) => { setStock(s.data || {}); setItems(i.data || []); })
      .catch(err => setError(err.response?.data?.error || 'Failed to load stock'))
      .finally(() => setLoading(false));
  }, []);

  const nameOf = useMemo(() => Object.fromEntries(items.map(i => [i.itemNo, i.itemName])), [items]);
  const typeOf = useMemo(() => Object.fromEntries(items.map(i => [i.itemNo, i.itemType])), [items]);

  const rows = useMemo(() => {
    const list = Object.entries(stock).map(([itemNo, s]) => ({
      itemNo, itemName: nameOf[itemNo] || itemNo, itemType: typeOf[itemNo] || '',
      qty: s.qty || 0, value: s.value || 0, avgCost: s.avgCost || 0,
      byLocation: s.byLocation || {}
    }));
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? list.filter(r => r.itemNo.toLowerCase().includes(needle) || r.itemName.toLowerCase().includes(needle))
      : list;
    return filtered.sort((a, b) => b.value - a.value);
  }, [stock, nameOf, typeOf, q]);

  const totals = useMemo(() => ({
    value: rows.reduce((s, r) => s + r.value, 0),
    lines: rows.length,
    negatives: rows.filter(r => r.qty < 0).length,
    zero: rows.filter(r => r.qty === 0).length,
  }), [rows]);

  const allLocations = useMemo(() => {
    const set = new Set();
    rows.forEach(r => Object.keys(r.byLocation).forEach(l => set.add(l)));
    return [...set].sort();
  }, [rows]);

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading stock…</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } }`}</style>

      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
            <Box display="flex" alignItems="center">
              <Warehouse sx={{ mr: 1, color: '#1565c0' }} />
              <Typography variant="h5" color="primary">Inventory — Valuation & Availability</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => window.print()} title="Print"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Print />
              </IconButton>
              <IconButton onClick={() => navigate('/supply-chain')} title="Supply Chain Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} sm={3}>
              <StatCard icon={Inventory2} label="Total Stock Value" color="#1565c0"
                value={`${sym()} ${fmt(totals.value)}`} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={Warehouse} label="Items in Stock" color="#2e7d32" value={totals.lines} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={Inventory2} label="Negative Stock" color="#c62828" value={totals.negatives} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={Inventory2} label="Zero Stock" color="#f57c00" value={totals.zero} />
            </Grid>
          </Grid>

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2} className="no-print">
            <ToggleButtonGroup size="small" exclusive value={mode}
              onChange={(e, v) => v && setMode(v)}>
              <ToggleButton value="valuation">Valuation</ToggleButton>
              <ToggleButton value="availability">Availability by Location</ToggleButton>
            </ToggleButtonGroup>
            <TextField size="small" placeholder="Search item…" value={q}
              onChange={e => setQ(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 0.5, color: 'text.disabled' }} fontSize="small" /> }} />
          </Box>

          {rows.length === 0 ? <Typography>No stock on hand.</Typography> : mode === 'valuation' ? (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Item No</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="right"><strong>Qty on Hand</strong></TableCell>
                    <TableCell align="right"><strong>Avg Unit Cost</strong></TableCell>
                    <TableCell align="right"><strong>Total Value</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.itemNo} hover
                      sx={{ bgcolor: r.qty < 0 ? 'error.50' : undefined }}>
                      <TableCell><strong>{r.itemNo}</strong></TableCell>
                      <TableCell>{r.itemName}</TableCell>
                      <TableCell align="right" sx={{ color: r.qty < 0 ? 'error.main' : undefined, fontWeight: r.qty < 0 ? 700 : 400 }}>
                        {fmt(r.qty)}
                      </TableCell>
                      <TableCell align="right">{fmt(r.avgCost)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.value)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: 800 }}>Total Inventory Value</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{sym()} {fmt(totals.value)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 30 }} />
                    <TableCell><strong>Item No</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    {allLocations.map(l => <TableCell key={l} align="right"><strong>{l}</strong></TableCell>)}
                    <TableCell align="right"><strong>Total Qty</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => (
                    <React.Fragment key={r.itemNo}>
                      <TableRow hover sx={{ cursor: 'pointer' }}
                        onClick={() => setOpen(o => ({ ...o, [r.itemNo]: !o[r.itemNo] }))}>
                        <TableCell>
                          {open[r.itemNo] ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                        </TableCell>
                        <TableCell><strong>{r.itemNo}</strong></TableCell>
                        <TableCell>{r.itemName}</TableCell>
                        {allLocations.map(l => {
                          const cell = r.byLocation[l];
                          return (
                            <TableCell key={l} align="right"
                              sx={{ color: cell && cell.qty < 0 ? 'error.main' : cell ? undefined : 'text.disabled' }}>
                              {cell ? fmt(cell.qty) : '—'}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(r.qty)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={allLocations.length + 4} sx={{ py: 0, border: 0 }}>
                          <Collapse in={!!open[r.itemNo]} unmountOnExit>
                            <Box sx={{ py: 1, pl: 4 }}>
                              {Object.entries(r.byLocation).length === 0
                                ? <Typography variant="caption" color="text.secondary">No location-stamped stock.</Typography>
                                : Object.entries(r.byLocation).map(([loc, v]) => (
                                  <Chip key={loc} size="small" variant="outlined" sx={{ mr: 1, mb: 0.5 }}
                                    label={`${loc}: ${fmt(v.qty)} (${sym()} ${fmt(v.value)})`} />
                                ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InventoryValuation;
